/**
 * Demander quelque chose au client, depuis le dossier — chunk 9.AD.1.
 *
 * Un seul dialogue pour les deux gestes, parce que du point de vue du conseiller
 * c'est le même : « j'ai besoin de ça, je le demande à cette personne ».
 *
 *   - un requis de formulaire → on envoie le formulaire ;
 *   - tout autre requis d'audience `client` → on demande l'information.
 *
 * ## Le destinataire est choisi, jamais saisi
 *
 * Décision PO : pas d'adresse libre dans ce chunk. La liste vient des contacts de
 * l'entreprise du dossier, et le serveur revérifie l'appartenance — l'interface
 * n'est pas la garde, elle est le confort.
 *
 * Un contact sans adresse reste **visible mais non sélectionnable**, avec la raison.
 * Le masquer ferait chercher au conseiller une personne qu'il sait exister.
 */

import { Schemas } from "@kollectivia/shared";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { InlineContactForm } from "../../../components/InlineContactForm";
import {
  useRequestInfoFromClient,
  useSendFormToClient,
} from "../../../lib/queries/clientCommunications";
import { useContacts } from "../../../lib/queries/contacts";

type Props = {
  readonly projectId: string;
  readonly companyId: string | null;
  /** Le requis concerné. */
  readonly requirement: {
    id: string;
    kind: string;
    requirement_key: string;
    label: string;
  };
  /** L'item de travail — présent dès que le requis n'est pas satisfait. */
  readonly missingInformationId: string | null;
  readonly onClose: () => void;
  readonly onSent?: () => void;
  readonly initialContactId?: string | null;
  readonly existingIntakeLinkId?: string;
};

type ContactRow = {
  id: string;
  full_name?: string;
  email?: string | null;
  title?: string | null;
  is_primary?: boolean;
};

export function AskClientDialog({
  projectId,
  companyId,
  requirement,
  missingInformationId,
  onClose,
  onSent,
  initialContactId,
  existingIntakeLinkId,
}: Props) {
  const { t } = useTranslation();
  const idTitre = useId();
  const idContact = useId();
  const idNote = useId();

  const [addingContact, setAddingContact] = useState(false);
  const [contactTouched, setContactTouched] = useState(false);
  const [contactId, setContactId] = useState<string>(initialContactId ?? "");
  const [note, setNote] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);

  const contacts = useContacts(companyId ?? undefined, { limit: 100 });
  const demanderInfo = useRequestInfoFromClient(projectId);
  const envoyerFormulaire = useSendFormToClient(projectId);
  const enCours = demanderInfo.isPending || envoyerFormulaire.isPending;

  /**
   * 9.BJ.1 — un mandat s'envoie EXACTEMENT comme un formulaire.
   *
   * Sa `requirement_key` est un identifiant de version publiée, comme celle d'un
   * `form_response` : le lien magique se crée de la même façon, et le client
   * signe sur la même surface publique. Ne pas le brancher ici aurait laissé le
   * conseiller sans aucun geste pour demander le seul document qui débloque
   * l'envoi.
   */
  const estFormulaire = requirement.kind === "form_response" || requirement.kind === "mandate";

  const liste = useMemo(() => {
    const items = (contacts.data?.items ?? []) as ContactRow[];
    return items.map((c) => ({
      id: c.id,
      nom: c.full_name ?? "",
      email: (c.email ?? "").trim(),
      principal: c.is_primary === true,
    }));
  }, [contacts.data]);

  // Pré-sélection du contact principal : c'est presque toujours le bon, et
  // l'imposer ferait perdre un clic à chaque demande.
  useEffect(() => {
    if (contactId !== "" || contactTouched) return;
    const defaut =
      liste.find((c) => c.principal && c.email.length > 0) ?? liste.find((c) => c.email.length > 0);
    if (defaut) setContactId(defaut.id);
  }, [liste, contactId, contactTouched]);

  const choisi = liste.find((c) => c.id === contactId);
  const envoyable =
    choisi !== undefined &&
    Schemas.CreateContactSchema.safeParse({
      company_id: companyId,
      full_name: choisi.nom,
      email: choisi.email,
    }).success &&
    !enCours;

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    if (!envoyable) return;
    setErreur(null);
    const noteNettoyee = note.trim();
    try {
      if (estFormulaire) {
        await envoyerFormulaire.mutateAsync({
          project_id: projectId,
          contact_id: contactId,
          // La clé d'un requis de formulaire EST l'identifiant de la version
          // publiée. Le serveur crée le lien puis l'envoie, en une transaction.
          ...(existingIntakeLinkId
            ? { intake_link_id: existingIntakeLinkId }
            : { form_template_version_id: requirement.requirement_key }),
          ...(noteNettoyee.length > 0 ? { note: noteNettoyee } : {}),
        });
      } else {
        if (missingInformationId === null) {
          setErreur(t("client_comms.dialog.no_item"));
          return;
        }
        await demanderInfo.mutateAsync({
          missing_information_id: missingInformationId,
          contact_id: contactId,
          ...(noteNettoyee.length > 0 ? { note: noteNettoyee } : {}),
        });
      }
      onSent?.();
      onClose();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : t("client_comms.dialog.error"));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitre}
    >
      {addingContact && companyId ? (
        <div className="w-full max-w-md rounded-xl bg-white p-5">
          <h2 id={idTitre} className="mb-4 text-lg font-semibold">
            {t("request_flow.add_contact")}
          </h2>
          <InlineContactForm
            companyId={companyId}
            onCreated={(id) => {
              setContactId(id);
              setContactTouched(true);
              setAddingContact(false);
            }}
            onCancel={() => setAddingContact(false)}
          />
        </div>
      ) : (
        <form
          onSubmit={soumettre}
          className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-lg"
        >
          <h2 id={idTitre} className="text-base font-semibold text-slate-900">
            {estFormulaire
              ? t("client_comms.dialog.title_form")
              : t("client_comms.dialog.title_info")}
          </h2>
          <p className="mt-1 text-sm text-slate-600">{requirement.label}</p>

          <label htmlFor={idContact} className="mt-4 block text-sm font-medium text-slate-700">
            {t("client_comms.dialog.recipient")}
          </label>

          {contacts.isPending ? (
            <p className="mt-1 px-3 py-2 text-sm text-slate-500" aria-live="polite">
              {t("client_comms.dialog.loading_contacts")}
            </p>
          ) : liste.length === 0 ? (
            <p className="mt-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {t("client_comms.dialog.no_contact")}
            </p>
          ) : (
            <select
              id={idContact}
              value={contactId}
              onChange={(e) => {
                setContactId(e.target.value);
                setContactTouched(true);
              }}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
            >
              <option value="">{t("client_comms.dialog.choose_recipient")}</option>
              {liste.map((c) => (
                // Un contact sans adresse reste VISIBLE : le masquer ferait chercher
                // une personne dont le conseiller sait qu'elle existe.
                <option key={c.id} value={c.id} disabled={c.email.length === 0}>
                  {c.nom}
                  {c.email.length > 0
                    ? ` — ${c.email}`
                    : ` — ${t("client_comms.dialog.no_email_suffix")}`}
                </option>
              ))}
            </select>
          )}

          {companyId && (
            <button
              type="button"
              className="btn mt-3 min-h-11"
              onClick={() => setAddingContact(true)}
            >
              {t("request_flow.add_contact")}
            </button>
          )}

          <label htmlFor={idNote} className="mt-4 block text-sm font-medium text-slate-700">
            {t("client_comms.dialog.note")}
          </label>
          <textarea
            id={idNote}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={3}
            className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
          />
          <p className="mt-1 text-xs text-slate-500">{t("client_comms.dialog.note_hint")}</p>

          {erreur !== null && (
            <p className="mt-3 text-sm text-red-700" role="alert">
              {erreur}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={!envoyable}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 rounded-lg px-3 py-1.5 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {enCours ? t("client_comms.dialog.sending") : t("client_comms.dialog.send")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
